
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Mail, MapPin } from "lucide-react";
import { useState, useEffect } from "react";
import { submitToZoho } from "@/utils/secureFormSubmission";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    setIsSubmitting(true);
    try {
      const success = await submitToZoho({
        firstName: formData.name.split(' ')[0] || formData.name,
        lastName: formData.name.split(' ').slice(1).join(' ') || '',
        email: formData.email,
        company: formData.company,
        message: formData.message,
        formType: 'Contact Page'
      });

      if (success) {
        setSubmitted(true);
        setFormData({
          name: "",
          email: "",
          company: "",
          message: "",
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Optimized Hero Section with Background Image and Parallax */}
        <div className="relative py-40 md:py-48 lg:py-56 bg-cover bg-center bg-no-repeat min-h-[80vh] overflow-hidden">
          <picture className="absolute inset-0">
            <source 
              media="(max-width: 768px)" 
              srcSet="/lovable-uploads/3dfedd24-f17a-4bcc-ad42-291548c44174.png"
            />
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat will-change-transform"
              style={{
                backgroundImage: `url('/lovable-uploads/3dfedd24-f17a-4bcc-ad42-291548c44174.png')`,
                transform: `translate3d(0, ${scrollY * 0.5}px, 0)`,
                backfaceVisibility: 'hidden',
                perspective: '1000px'
              }}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
          <div className="container mx-auto px-4 md:px-6 relative z-10 h-full flex items-center">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-2xl">Get in Touch</h1>
              <p className="text-xl md:text-2xl text-white/90 drop-shadow-lg">
                We'd love to hear from you. Contact us for more information about our products and services.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Content */}
        <div className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                <div className="bg-slate-50 p-6 rounded-lg text-center">
                  <div className="w-12 h-12 bg-[#8932c8]/10 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-[#8932c8]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">Email Us</h3>
                  <a href="mailto:sales@spharaka.com" className="text-[#8932c8] hover:underline block">sales@spharaka.com</a>
                  <p className="text-sm text-gray-600 mt-1">For sales inquiries and general information</p>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg text-center">
                  <div className="w-12 h-12 bg-[#8932c8]/10 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-[#8932c8]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">Hyderabad Office</h3>
                  <p className="text-gray-700 text-sm">
                    Spharaka Networks Private Limited<br />
                    MIG -5, 1st Floor, Above Peter England,<br />
                    Road no 1, KPHB Colony,<br />
                    Hyderabad - 500072
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg text-center">
                  <div className="w-12 h-12 bg-[#8932c8]/10 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-[#8932c8]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">Bengaluru Office</h3>
                  <p className="text-gray-700 text-sm">
                    Spharaka Networks Private Limited<br />
                    BHIVE Platinum Church Street,<br />
                    48, Church Street, Haridevpura,<br />
                    Shanthala Nagar, Ashok Nagar,<br />
                    Bengaluru, Karnataka 560001
                  </p>
                </div>
              </div>

              <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="p-8 bg-[#8932c8]">
                    <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
                    <p className="text-white/90 mb-8">
                      Fill out the form and our team will get back to you within 24 hours. We're ready to help you enhance your security posture.
                    </p>
                    <div className="space-y-6">
                      <div className="flex items-center">
                        <Mail className="h-5 w-5 text-white mr-3" />
                        <span className="text-white">sales@spharaka.com</span>
                      </div>
                      <div className="flex items-start">
                        <MapPin className="h-5 w-5 text-white mr-3 mt-1" />
                        <div className="text-white">
                          <div className="mb-2">
                            <strong>Hyderabad Office:</strong><br />
                            MIG -5, 1st Floor, KPHB Colony, Hyderabad - 500072
                          </div>
                          <div>
                            <strong>Bengaluru Office:</strong><br />
                            BHIVE Platinum Church Street, Ashok Nagar, Bengaluru - 560001
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>
                    
                    {submitted ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                        <h3 className="text-xl font-semibold text-green-800 mb-2">Thank You!</h3>
                        <p className="text-green-700">
                          Your message has been sent successfully. A member of our team will be in touch with you soon.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                          />
                        </div>

                        <div>
                          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                          <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={4}
                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                            required
                          ></textarea>
                        </div>

                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            id="privacyConsent"
                            required
                            className="mt-1 mr-3 h-4 w-4 text-[#8932c8] focus:ring-[#8932c8] border-gray-300 rounded"
                          />
                          <label htmlFor="privacyConsent" className="text-sm text-gray-600">
                            I agree to the{" "}
                            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#8932c8] hover:underline">
                              Privacy Policy
                            </a>{" "}
                            and consent to the processing of my personal data. <span className="text-red-500">*</span>
                          </label>
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-[#8932c8] text-white py-3 px-4 rounded-md hover:bg-[#7a2cb3] transition-colors disabled:opacity-50"
                        >
                          {isSubmitting ? "Sending..." : "Send Message"}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
